import { useContext, useEffect, useState } from "react";
import { patchData } from "../backend";
import { PlusCircleIcon, CalendarIcon, PencilAltIcon, PencilIcon, CheckIcon, ChevronDownIcon, InformationCircleIcon, CogIcon, DuplicateIcon } from "@heroicons/react/outline";
import { SignatureField, SignatureConfig } from "../interfaces";
import { WorkflowContext, useModal } from "../contexts";
import { SourceField } from "../interfaces";
import { ICON_MAP, SINGATURE_FIELD_SIZE_MAP } from "../utils";
import WorkflowSignaturesConfigModal from "./WorkflowSignaturesConfigModal";
import { useTranslation } from "react-i18next";
import HeroIcon from "./HeroIcon";
import CopyToClipboard from "react-copy-to-clipboard";
import SmallTextInputField from "./SmallTextInputField";
import WorkflowModalHubspotPropertiesCaptureList from "./WorkflowModalHubspotPropertiesCaptureList";

interface Props {
  recipientNumber: number;
  signatureConfig: SignatureConfig;
}

function WorkflowSignatureFieldsInline(props: Props) {
  const { recipientNumber, signatureConfig } = props;
  const { t } = useTranslation();

  const workflowContext = useContext(WorkflowContext);
  const { workflow, mutateWorkflow } = workflowContext;

  const { openModal } = useModal();

  const [copiedTag, setCopiedTag] = useState<string>();
  const [openCaptureField, setOpenCaptureField] = useState<string>();

  const [currentCapturePlaceholder, setCurrentCapturePlaceholder] = useState<string>("");
  const [currentCaptureIsRequired, setCurrentCaptureIsRequired] = useState<boolean>(false);

  const [clickedField, setClickedField] = useState<SignatureField | "TEXT_REQUIRED" | "TEXTAREA_REQUIRED" | "CHECKBOX_REQUIRED">();

  useEffect(() => {
    if (!openCaptureField)
      return;

    const field = workflow.source?.sourceFields.find(f => f.id === openCaptureField);

    setCurrentCapturePlaceholder(field?.capturePlaceholder || "");
    setCurrentCaptureIsRequired(!!field?.captureIsRequired);

  }, [openCaptureField, workflow.source?.sourceFields]);

  async function handleCopySignatureField(field: SignatureField, recipient: number, required: boolean = false) {
    if (clickedField)
      return; // Prevent action while a field is already being inserted

    setClickedField(field + (required ? "_REQUIRED" : "") as SignatureField);

    const type = field.toLocaleLowerCase();
    const flag = required ? "-required" : "";
    const path = `https://static.portant.co/field-${type}${flag}-u${recipient}.png`;

    const [width, height] = SINGATURE_FIELD_SIZE_MAP[field];

    // Build the HTML clipboard payload as a Promise<Blob> and hand it straight to
    // ClipboardItem so navigator.clipboard.write is invoked synchronously inside the
    // click handler. Awaiting fetch/blob/FileReader before calling write expires the
    // transient user-activation gesture on Safari, causing a NotAllowedError.
    const htmlBlobPromise = (async () => {
      const img = await fetch(path);
      const imgBlob = await img.blob();

      const imageUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(imgBlob);
      });

      const htmlBlob = `<span style="border:none;display:inline-block;overflow:hidden;width:${width}px;height:${height}px;"><img src=${imageUrl} width="${width}" height="${height}" style="margin-left:0px;margin-top:0px;"></span>`

      return new Blob([htmlBlob], { type: "text/html" });
    })();

    try {
      await navigator.clipboard.write([
        new ClipboardItem({ 'text/html': htmlBlobPromise })
      ]);
    } catch (error) {
      console.error(error);
    }

    setTimeout(() => setClickedField(undefined), 1000);
  }

  function handleCopyTag(tag: string) {
    setCopiedTag(tag);
    setTimeout(() => setCopiedTag(undefined), 2000);
  }

  function updateField(fieldId: string, updates: Partial<SourceField>) {
    patchData(`/workflows/source-fields/${fieldId}/`, updates)
      .catch(console.error);
  }

  return (
    <div className={`flex flex-col`}>
      <div className="flex items-center text-sm border-b px-2 py-1">
        <PencilAltIcon className="w-4 h-4 mr-2" />
        <div className="font-semibold whitespace-nowrap py-2">
          {t("Signature")}
        </div>
        <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "SIGNATURE" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleCopySignatureField("SIGNATURE", recipientNumber)}>
          {clickedField === "SIGNATURE" ? t("Copied") : <><DuplicateIcon className="w-4 h-4 mr-1" /> {t("Copy")}</>}
        </button>

      </div>

      <div className="flex items-center text-sm border-b px-2 py-1">
        <div className="w-4 h-4 mr-2 font-gilroy text-sm underline">
          AB
        </div>
        <div className="font-semibold whitespace-nowrap py-2">
          {t("Initials")}
        </div>
        <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "INITIALS" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleCopySignatureField("INITIALS", recipientNumber)}>
          {clickedField === "INITIALS" ? t("Copied") : <><DuplicateIcon className="w-4 h-4 mr-1" /> {t("Copy")}</>}
        </button>
      </div>

      <div className="flex items-center text-sm border-b px-2 py-1">
        <div className="w-4 h-4 mr-2 font-gilroy text-sm underline">
          Tt
        </div>
        <div className="flex items-center gap-2 font-semibold whitespace-nowrap py-2">
          {t("Text")}
          <div className="text-xs text-gray-400 font-semibold">
            {t("Optional")}
          </div>
        </div>
        <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "TEXT" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleCopySignatureField("TEXT", recipientNumber)}>
          {clickedField === "TEXT" ? t("Copied") : <><DuplicateIcon className="w-4 h-4 mr-1" /> {t("Copy")}</>}
        </button>
      </div>

      <div className="flex items-center text-sm border-b px-2 py-1">
        <div className="w-4 h-4 mr-2 font-gilroy text-sm underline decoration-red">
          Tt
        </div>
        <div className="flex items-center gap-2 font-semibold whitespace-nowrap py-2">
          {t("Text")}
          <div className="text-xs text-red/50 font-semibold">
            {t("Required")}
          </div>
        </div>
        <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "TEXT_REQUIRED" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleCopySignatureField("TEXT", recipientNumber, true)}>
          {clickedField === "TEXT_REQUIRED" ? t("Copied") : <><DuplicateIcon className="w-4 h-4 mr-1" /> {t("Copy")}</>}
        </button>
      </div>

      <div className="flex items-center text-sm border-b px-2 py-1">
        <div className="w-4 h-4 mr-2 font-gilroy text-xs flex-shrink-0 text-center border border-black rounded-sm">
          Tt
        </div>
        <div className="flex items-center gap-2 font-semibold whitespace-nowrap py-2">
          {t("Textarea")}
          <div className="text-xs text-gray-400 font-semibold">
            {t("Optional")}
          </div>
        </div>
        <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "TEXTAREA" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleCopySignatureField("TEXTAREA", recipientNumber)}>
          {clickedField === "TEXTAREA" ? t("Copied") : <><DuplicateIcon className="w-4 h-4 mr-1" /> {t("Copy")}</>}
        </button>
      </div>

      <div className="flex items-center text-sm border-b px-2 py-1">
        <div className="w-4 h-4 mr-2 font-gilroy text-xs flex-shrink-0 text-center  border border-red rounded-sm">
          Tt
        </div>
        <div className="flex items-center gap-2 font-semibold whitespace-nowrap py-2">
          {t("Textarea")}
          <div className="text-xs text-red/50 font-semibold">
            {t("Required")}
          </div>
        </div>
        <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "TEXTAREA_REQUIRED" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleCopySignatureField("TEXTAREA", recipientNumber, true)}>
          {clickedField === "TEXTAREA_REQUIRED" ? t("Copied") : <><DuplicateIcon className="w-4 h-4 mr-1" /> {t("Copy")}</>}
        </button>
      </div>

      <div className="flex items-center text-sm border-b px-2 py-1 last:border-b-0">
        <CheckIcon className="w-4 h-4 mr-2 border border-black rounded" />
        <div className="flex items-center gap-2 font-semibold whitespace-nowrap py-2">
          {t("Checkbox")}
          <div className="text-xs text-gray-400 font-semibold">
            {t("Optional")}
          </div>
        </div>
        <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "CHECKBOX" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleCopySignatureField("CHECKBOX", recipientNumber)}>
          {clickedField === "CHECKBOX" ? t("Copied") : <><DuplicateIcon className="w-4 h-4 mr-1" /> {t("Copy")}</>}
        </button>
      </div>

      <div className="flex items-center text-sm border-b px-2 py-1 last:border-b-0">
        <CheckIcon className="w-4 h-4 mr-2 border border-red rounded" />
        <div className="flex items-center gap-2 font-semibold whitespace-nowrap py-2">
          {t("Checkbox")}
          <div className="text-xs text-red/50 font-semibold">
            {t("Required")}
          </div>
        </div>
        <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "CHECKBOX_REQUIRED" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleCopySignatureField("CHECKBOX", recipientNumber, true)}>
          {clickedField === "CHECKBOX_REQUIRED" ? t("Copied") : <><DuplicateIcon className="w-4 h-4 mr-1" /> {t("Copy")}</>}
        </button>
      </div>

      <div className="flex items-center text-sm border-b px-2 py-1">
        <div className="flex flex-col">
          <div className="flex items-center">
            <CalendarIcon className="w-4 h-4 mr-2" />
            <div className="font-semibold whitespace-nowrap py-2">
              {t("Date")}
            </div>
            <div className="text-xs ml-auto flex items-start group cursor-pointer hover:text-blue hover:underline" onClick={() => openModal(<WorkflowSignaturesConfigModal {...workflowContext} />)}>
              {workflow.signatureConfig!.dateFormat || workflow.owner?.dateFormat}
              <div className="ml-1">
                {(signatureConfig.dateTimeFormat || workflow.owner?.timeFormat) === "HOUR_12" && "12Hr"}
                {(signatureConfig.dateTimeFormat || workflow.owner?.timeFormat) === "HOUR_24" && "24Hr"}
              </div>
              <PencilIcon className="opacity-0 group-hover:opacity-100 w-3 h-3 ml-1" />
            </div>
            <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "DATE" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleCopySignatureField("DATE", recipientNumber)}>
              {clickedField === "DATE" ? t("Copied") : <><DuplicateIcon className="w-4 h-4 mr-1" /> {t("Copy")}</>}
            </button>
          </div>
          <div className="text-xs mt-1">{t("Automatically populated with the current date of when the recipient completes the document.")}</div>
        </div>
      </div>

      {/* @ts-ignore-next-line */}
      {(workflow.source?.sourceType === "HUBSPOT" && workflow.signatureConfig["recipientEmail" + props.recipientNumber]) &&
        <>
          <div className="flex items-center text-sm px-2 py-1 bg-gray-100 font-semibold font-gilroy text-gray-600">
            HubSpot Capture Fields
            <div className="ml-auto mr-2 relative group">
              <InformationCircleIcon className={`h-4 w-4 text-gray-600 cursor-default`} />
              <div className="tooltip group-hover:visible right-2 top-4 text-wrap w-60 rounded-tr-none">
                {t("Insert fields into the signable document to capture data that is written to the contact in HubSpot.")}
              </div>
            </div>
            <CogIcon className="h-4 w-4 mr-2 text-gray-600 cursor-pointer" onClick={() => openModal(<WorkflowModalHubspotPropertiesCaptureList workflow={workflow} refreshWorkflow={mutateWorkflow} onClose={() => { mutateWorkflow(); }} />)} />
          </div>

          {workflow.source?.sourceFields.filter(field => workflow?.source?.hubspotConfig?.hubspotCaptureProperties.includes(field.fieldId))
            .map((field: SourceField, index: number) => (
              <div key={field.id} className="flex flex-col justify-center py-1 px-2 text-sm border-b last:border-b-0 w-full">
                <div className="flex w-full items-center gap-1">
                  <ChevronDownIcon className={`w-4 h-4 flex-shrink-0 transform transition-all cursor-pointer ${openCaptureField === field.id ? "rotate-0" : "-rotate-90"}`} onClick={() => setOpenCaptureField(openCaptureField === field.id ? undefined : field.id)} />
                  <div className="font-semibold pt-2 pb-1 truncate">
                    {field.fieldName}
                  </div>
                  {field.captureIsRequired &&
                    <span className="text-red -ml-1">*</span>
                  }
                  <div className="ml-auto relative group">
                    <HeroIcon icon={ICON_MAP[field.fieldType][0]} className={`w-4 h-4 flex-shrink-0 cursor-pointer ${!field.customMapping ? "text-gray-600" : "text-blue"}`} />
                    <div className="hidden group-hover:flex absolute -top-1 right-6 bg-black text-white text-xs rounded-sm z-10 px-2 py-1 whitespace-nowrap select-none">
                      <svg height="8px" width="4px" xmlns="http://www.w3.org/2000/svg" className="absolute text-black" style={{ right: "-4px", top: "calc(100% / 2 - 4px)" }}>
                        <polygon points="0,0 4,4 0,8" fill="currentColor" />
                      </svg>
                      {ICON_MAP[field.fieldType][1]}
                    </div>
                  </div>
                  <CopyToClipboard text={`[[${recipientNumber}: ${field.fieldName}]]`} onCopy={() => handleCopyTag(field.fieldId)}>
                    <button className={`ml-2 btn btn-white px-2 py-1 w-20 flex-shrink-0 ${copiedTag === field.fieldId ? "bg-blue text-white" : "bg-white text-blue"}`} onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                      {copiedTag === field.fieldId ? t("Copied!") : <><DuplicateIcon className="w-4 h-4 mr-1" /> {t("Copy")}</>}
                    </button>
                  </CopyToClipboard>
                </div>
                {openCaptureField === field.id &&
                  <div className="flex flex-col gap-1 mt-1">
                    {field.fieldType !== "CHECKBOX" &&
                      <SmallTextInputField className="mb-2" label={t("Placeholder")} value={currentCapturePlaceholder} placeholder={"Ab"} onChange={setCurrentCapturePlaceholder} onEnter={() => updateField(field.id, { capturePlaceholder: currentCapturePlaceholder })} />
                    }
                    <label className="flex items-center gap-1 font-semibold text-gray-600 text-sm">
                      <input type="checkbox" className="mb-0.5" checked={currentCaptureIsRequired} onChange={e => { setCurrentCaptureIsRequired(e.currentTarget.checked); updateField(field.id, { captureIsRequired: e.currentTarget.checked }) }} />
                      {field.fieldType !== "CHECKBOX"
                        ? t("Require recipient to fill this field")
                        : t("Require recipient to check this field")
                      }
                    </label>
                  </div>
                }
              </div>
            ))}

          <div className="flex justify-center py-2 px-2 text-sm border-b last:border-b-0 items-center gap-1 text-blue">
            <PlusCircleIcon className="w-4 h-4" />
            <div className="flex items-center gap-1 font-semibold font-gilroy cursor-pointer hover:underline" onClick={() => openModal(<WorkflowModalHubspotPropertiesCaptureList workflow={workflow} refreshWorkflow={mutateWorkflow} onClose={() => { mutateWorkflow(); }} />)}>
              {t("Select HubSpot Fields")}
            </div>
          </div>
        </>
      }
    </div>
  );
}

export default WorkflowSignatureFieldsInline;
